import { useCallback, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useResellerContext } from '@/lib/reseller-context-store';
import { cn } from '@/lib/cn';
import { logout } from '@/api/auth';
import {
  LayoutDashboard, Users, Building2, Briefcase, Settings, MessageSquare, User as UserIcon,
  LogOut, Menu, X, Mic, Network, Hash, PhoneCall, Radio, Volume2,
  CreditCard, Zap, Receipt, BarChart3, BookOpen, GraduationCap, Palette, Coins,
  ClipboardList, Package, ShoppingCart, Megaphone, HardDrive,
  Grid3x3, Library, FolderOpen, Voicemail, Mail, Bookmark, Clock,
  ChevronRight, UserSquare, Globe, KeyRound,
} from 'lucide-react';
import { ForcePasswordChangeGuard } from '@/components/ForcePasswordChangeGuard';
import { CustomerContextBanner } from '@/components/CustomerContextBanner';
import { ResellerContextBanner } from '@/components/ResellerContextBanner';
import { MasqueradeBanner } from '@/components/MasqueradeBanner';
import { DeployStatusBanner } from '@/components/DeployStatusBanner';
import { useBranding } from '@/hooks/useBranding';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMyModulePermissions } from '@/hooks/useMyModulePermissions';
import { useSmsUnread } from '@/hooks/useSmsUnread';
import { Toaster } from '@/components/ui/toaster';
import { scopedHref } from '@/hooks/useScopedNavigate';
import { ToggleLeft, ShieldCheck } from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  show: (role: string, isSa: boolean) => boolean;
  /** Optional feature-flag key — nav item hidden when flag is off. Super-admin
   *  always bypasses (they need to manage features even when disabled). */
  feature?: string;
  /** Marks this nav item as a customer-scoped page. Super-admins only see it
   *  once they have drilled into a reseller AND a customer — otherwise the
   *  underlying route will just bounce them back to /resellers or /customers.
   *  reseller_owner only needs a customer context (their reseller is implicit)
   *  so we only hide it when they have no customer context. */
  customerScoped?: boolean;
  /** Reseller-level settings that should be hidden once the user has drilled
   *  into a customer context — e.g. branding and reseller email belong to the
   *  reseller, not the customer they are currently managing. */
  hideInCustomerContext?: boolean;
  /** Platform-owner-only items (System Prompts, AWS Polly, Recording storage,
   *  Stripe, Plans, etc.). Super_admin sees these in the cross-platform view,
   *  but they're hidden once super_admin "enters" a specific reseller — at
   *  that point the sidebar should focus on managing THAT reseller's world,
   *  not the platform globals. Has no effect on non-super_admin roles. */
  platformOnly?: boolean;
  /** Items that should ALWAYS appear in the sidebar regardless of which
   *  scope (platform / reseller / customer) the user is currently inside.
   *  Reserved for things that aren't tied to a tenant scope at all — the
   *  user's own account, the docs site, the dashboard. Without this flag
   *  these items would be filtered out by the customer-context narrowing
   *  that hides anything not marked `customerScoped: true`. */
  alwaysVisible?: boolean;
}

interface NavGroup {
  title: string | null; // null = no visible group heading (first group)
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: null,
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: () => true, alwaysVisible: true },
    ],
  },
  {
    title: 'Accounts',
    items: [
      // office_manager manages users from the Users card on /account instead
      // of a dedicated sidebar tab (Sam 2026-07-02). The /users routes stay
      // reachable (detail + new-user pages are linked from that card) — only
      // this nav entry is hidden for them.
      { to: '/users', label: 'Users', icon: Users, show: (role) => ['super_admin', 'reseller_owner'].includes(role) },
      { to: '/resellers', label: 'Resellers', icon: Building2, show: (_r, sa) => sa, platformOnly: true },
      { to: '/customers', label: 'Customers', icon: Briefcase, show: (role, sa) => sa || role === 'reseller_owner' },
      {
        to: '/caller-profiles',
        label: 'Callers',
        icon: UserIcon,
        show: (role) => ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        customerScoped: true,
      },
      {
        to: '/caller-bookmarks',
        label: 'Bookmarks',
        icon: Bookmark,
        // hidden per Sam 2026-07-02 — temporary, bring back later. Customer
        // roles (office_manager and below, plus agent) lose the nav entry;
        // super_admin + reseller_owner keep it. The /caller-bookmarks route
        // itself still allows office_manager so direct links keep working.
        // Backlog note: docs/backlog.md "Bookmarks nav entry for customer
        // roles — temporarily hidden (2026-07-02)".
        show: (role) => ['super_admin', 'reseller_owner'].includes(role),
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Telephony',
    items: [
      { to: '/recordings', label: 'Recordings', icon: Mic, show: (role) => ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role), feature: 'recordings', customerScoped: true },
      {
        to: '/folders',
        label: 'Folders',
        icon: FolderOpen,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'recordings',
        customerScoped: true,
      },
      { to: '/ivr/trees', label: 'IVR Trees', icon: Network, show: () => true, feature: 'ivr_tree_builder', customerScoped: true },
      {
        to: '/time-frames',
        label: 'Time Frames',
        icon: Clock,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        customerScoped: true,
      },
      // DIDs + Carriers are reseller-and-above only. Customer roles see their
      // numbers read-only on /account (no management UI). See pickup 2026-04-20 §3.
      {
        to: '/dids',
        label: 'DIDs',
        icon: Hash,
        show: (role: string) => ['super_admin', 'reseller_owner'].includes(role),
        feature: 'dids',
      },
      {
        to: '/carriers',
        label: 'Carriers',
        icon: Radio,
        show: (role: string) => ['super_admin', 'reseller_owner'].includes(role),
      },
      {
        to: '/voicemails',
        label: 'Voicemail',
        icon: Voicemail,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'voicemail',
        customerScoped: true,
      },
      {
        to: '/conference-rooms',
        label: 'Conference rooms',
        icon: Users,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'conference',
        customerScoped: true,
      },
      {
        to: '/courses',
        label: 'Courses',
        icon: Library,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'courses',
        customerScoped: true,
      },
      {
        to: '/forum',
        label: 'Public forum',
        icon: Megaphone,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'public_forum',
        customerScoped: true,
      },
    ],
  },
  {
    title: 'PBX',
    items: [
      {
        to: '/extensions',
        label: 'Extensions',
        icon: PhoneCall,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'extensions',
        customerScoped: true,
      },
      {
        to: '/ring-groups',
        label: 'Ring groups',
        icon: Users,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'ring_groups',
        customerScoped: true,
      },
      {
        // Contacts directory (migration 0085) — per-customer phonebook
        // used as the internal-CID source by extension and ring_group
        // nodes. Renamed to "Internal contacts" (2026-05-18) and moved
        // under PBX with its consumers.
        to: '/contacts',
        label: 'Internal contacts',
        icon: UserSquare,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Shop',
    items: [
      {
        to: '/products',
        label: 'Products',
        icon: Package,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'shop',
        customerScoped: true,
      },
      {
        to: '/orders',
        label: 'Orders',
        icon: ShoppingCart,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'shop',
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Engagement',
    items: [
      {
        to: '/sms',
        // Covers the whole SMS suite (2026-07): conversations, broadcasts,
        // lists + drip campaigns, keywords — all tabs on the /sms page.
        label: 'SMS & campaigns',
        icon: MessageSquare,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'sms',
        customerScoped: true,
      },
      {
        to: '/points',
        label: 'Points & credits',
        icon: Coins,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'points',
        customerScoped: true,
      },
      {
        to: '/quizzes',
        label: 'Quizzes',
        icon: GraduationCap,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'quizzes',
        customerScoped: true,
      },
      {
        to: '/surveys',
        label: 'Surveys',
        icon: ClipboardList,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'surveys',
        customerScoped: true,
      },
      {
        // Petition by phone (added 2026-05-18). Callers register their
        // support for a campaign over the phone — captures caller ID +
        // optional spoken comment + flexible signature fields. Feature
        // is shipped as a stub placeholder page; the IVR node, capture
        // flow, and reporting are queued for the next pass.
        to: '/petitions',
        label: 'Petitions',
        icon: ClipboardList,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'petitions',
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Reports',
    items: [
      {
        to: '/analytics/ivr',
        label: 'IVR analytics',
        icon: BarChart3,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'].includes(role),
        feature: 'ivr_tree_builder',
        customerScoped: true,
      },
      {
        to: '/call-log',
        label: 'Call log',
        icon: PhoneCall,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager'].includes(role),
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Billing',
    items: [
      { to: '/billing', label: 'Billing', icon: CreditCard, show: (role: string) => role === 'reseller_owner' },
      { to: '/billing/invoices', label: 'Invoices', icon: Receipt, show: (role: string) => role === 'reseller_owner' },
      {
        to: '/payment-gateways',
        label: 'Payment gateways',
        icon: CreditCard,
        show: (role: string) => ['super_admin', 'reseller_owner', 'office_manager'].includes(role),
        feature: 'payments',
        customerScoped: true,
      },
      {
        to: '/payment-dashboard',
        label: 'Payment dashboard',
        icon: BarChart3,
        show: (role: string) => ['super_admin', 'reseller_owner', 'office_manager', 'standard_user'].includes(role),
        feature: 'payments',
        customerScoped: true,
      },
      {
        to: '/payment-settings',
        label: 'Payment settings',
        icon: BarChart3,
        show: (role: string) => ['super_admin', 'reseller_owner', 'office_manager'].includes(role),
        feature: 'payments',
        customerScoped: true,
      },
      {
        to: '/pricing-rules',
        label: 'Pricing rules',
        icon: BarChart3,
        show: (role: string) => ['super_admin', 'reseller_owner'].includes(role),
      },
      {
        to: '/charges',
        label: 'Charge history',
        icon: Receipt,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user', 'limited_user'].includes(role),
        feature: 'payments',
        customerScoped: true,
      },
      {
        to: '/recurring',
        label: 'Recurring',
        icon: Zap,
        show: (role: string) =>
          ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user', 'limited_user'].includes(role),
        feature: 'payments',
        customerScoped: true,
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      {
        to: '/settings/features',
        label: 'Feature flags',
        icon: ToggleLeft,
        // Reseller/admin-only per Sam 2026-07-02 — customer-tier roles
        // (office_manager included) must not see or manage feature flags.
        show: (role: string, sa: boolean) => sa || role === 'reseller_owner',
        // Inside an entered customer this page (reseller-level DEFAULTS)
        // is the wrong surface and confused testing — the per-customer
        // switches live on the customer's Manage page. Only offer the
        // defaults page outside customer context (Sam 2026-08-06).
        hideInCustomerContext: true,
      },
      // Inside a customer context the same label points at THAT customer's
      // Manage page (features card) instead — href overridden in the render
      // loop, same two-entry pattern as DTMF profiles above.
      {
        to: '/settings/features',
        label: 'Feature flags',
        icon: ToggleLeft,
        show: (role: string, sa: boolean) => sa || role === 'reseller_owner',
        customerScoped: true,
      },
      { to: '/system-prompts', label: 'System Prompts', icon: Volume2, show: (_r: string, sa: boolean) => sa, platformOnly: true },
      {
        to: '/auth-lists',
        label: 'Auth lists',
        icon: ShieldCheck,
        show: (role: string) => ['super_admin', 'reseller_owner', 'office_manager', 'standard_user', 'agent'].includes(role),
        feature: 'auth_lists',
        customerScoped: true,
      },
      // DTMF profiles — two nav entries because the destination differs by
      // tier (Sam 2026-07-02: nobody should land on a nav item that bounces
      // to dashboard):
      //   • super_admin (platform view) → /settings/dtmf-profiles edits the
      //     PLATFORM defaults. platformOnly so it disappears once SA drills
      //     into a reseller (per-reseller overrides live on /resellers/:id).
      //   • office_manager → same flat URL; the backend scopes customer
      //     roles to their own customer, so the page renders THEIR
      //     customer-level overrides. Route now allows office_manager.
      //   • reseller_owner / SA inside a customer context → the second,
      //     customerScoped entry below, whose href is overridden in the
      //     render loop to /customers/:id/settings/dtmf-profiles (the
      //     existing SA+RO per-customer editor).
      // The two entries are never visible simultaneously (context filters
      // hide one or the other), so the shared `to` key never collides.
      {
        to: '/settings/dtmf-profiles',
        label: 'DTMF profiles',
        icon: Grid3x3,
        show: (role: string, sa: boolean) => sa || role === 'office_manager',
        platformOnly: true,
      },
      {
        to: '/settings/dtmf-profiles',
        label: 'DTMF profiles',
        icon: Grid3x3,
        show: (role: string, sa: boolean) => sa || role === 'reseller_owner',
        customerScoped: true,
      },
      { to: '/settings/email', label: 'Email settings', icon: Settings, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/aws-polly', label: 'AWS Polly (TTS)', icon: Volume2, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/cnam', label: 'Caller Name (CNAM)', icon: Settings, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/phone-defaults', label: 'Default country code', icon: Globe, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/sms', label: 'SMS (Telnyx)', icon: MessageSquare, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/tts-cache', label: 'TTS cache stats', icon: Volume2, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/storage', label: 'Recording storage (R2/S3)', icon: HardDrive, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/billable-items', label: 'Billable items', icon: CreditCard, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/reseller-email', label: 'Email settings', icon: Settings, show: (role) => role === 'reseller_owner', hideInCustomerContext: true },
      { to: '/settings/email-templates', label: 'Email templates', icon: Mail, show: (role, sa) => sa || role === 'reseller_owner' },
      { to: '/settings/branding', label: 'Brand identity', icon: Palette, show: (role) => ['super_admin', 'reseller_owner'].includes(role), hideInCustomerContext: true },
      // Reseller-level recording config: dial-in number + recycle-bin
      // retention max + per-30-day unit price. Super_admin gets a redirect
      // back to /resellers (they manage many resellers, not one self).
      { to: '/settings/recording', label: 'Recording config', icon: PhoneCall, show: (role) => role === 'reseller_owner', hideInCustomerContext: true },
      // Customer-level phone-recording settings: hangup auto-save vs
      // discard, recycle-bin retention slider, AND the registered admin
      // phone numbers (with OTP verify). Office_manager sees their own
      // customer; super_admin / reseller_owner see whichever customer
      // they entered via the customer-context pattern.
      // customerScoped: true → hidden in sidebar for super_admin /
      // reseller_owner until they enter a customer (matches the
      // platform-wide pattern for customer-account settings).
      { to: '/settings/recording-admins', label: 'Phone recording', icon: PhoneCall, show: (role) => ['super_admin', 'reseller_owner', 'office_manager'].includes(role), customerScoped: true },
      // Phone admin IVR console audit log — visible to anyone who can view
      // customer settings. Customer-scoped (the log is per-customer).
      { to: '/settings/audit-log', label: 'Admin audit log', icon: ClipboardList, show: (role) => ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user', 'limited_user'].includes(role), customerScoped: true },
      // Public-API keys. alwaysVisible because the page is meaningful in
      // EVERY scope: outside a customer context it mints reseller/platform
      // keys, inside one it mints customer keys — the page adapts itself.
      // Role gate + the 'api' feature flag still apply.
      { to: '/settings/api-keys', label: 'API keys', icon: KeyRound, show: (role) => ['super_admin', 'reseller_owner', 'office_manager'].includes(role), feature: 'api', alwaysVisible: true },
      { to: '/settings/contact-submissions', label: 'Contact submissions', icon: MessageSquare, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/stripe', label: 'Stripe', icon: Zap, show: (_r, sa) => sa, platformOnly: true },
      { to: '/settings/billing', label: 'Billing overview', icon: BarChart3, show: (_r, sa) => sa, platformOnly: true },
      { to: '/plans', label: 'Plans', icon: CreditCard, show: (_r, sa) => sa, platformOnly: true },
      { to: '/account', label: 'Account', icon: UserIcon, show: () => true, alwaysVisible: true },
    ],
  },
  {
    title: 'Help',
    items: [
      { to: '/docs', label: 'Documentation', icon: BookOpen, show: () => true, alwaysVisible: true },
    ],
  },
];

// localStorage key for the per-user expanded/collapsed state of sidebar
// groups. The value is a Record<groupTitle, boolean>; missing keys fall
// back to "open if this group contains the active route, closed
// otherwise" so a fresh user lands on a clean sidebar and the group
// for their current page is auto-open.
const SIDEBAR_GROUPS_LS_KEY = 'ivrello:sidebar-groups';

// ── Per-user permission scopes per nav path ─────────────────────────────────
// For agents / limited users, a nav item is hidden when its module scope has
// `read` EXPLICITLY set to false in their permission matrix (absent = shown —
// mirrors the backend's requireModulePermission default-allow-read). Keyed by
// NavItem.to so new nav entries added to NAV_GROUPS don't need touching:
// unmapped paths are simply never permission-filtered.
const NAV_PERMISSION_SCOPES: Record<string, string> = {
  '/recordings': 'recordings',
  '/folders': 'folders',
  '/ivr/trees': 'ivr',
  '/time-frames': 'telephony',
  '/voicemails': 'voicemail',
  '/conference-rooms': 'conference',
  '/caller-profiles': 'callers',
  '/caller-bookmarks': 'callers',
  '/auth-lists': 'auth_lists',
  '/contacts': 'contacts',
  '/extensions': 'telephony',
  '/ring-groups': 'telephony',
  '/products': 'shop',
  '/orders': 'shop',
  '/sms': 'sms',
  '/points': 'points',
  '/quizzes': 'quizzes',
  '/surveys': 'surveys',
  '/courses': 'courses',
  '/forum': 'forum',
  '/analytics/ivr': 'reports',
  '/call-log': 'reports',
  '/billing': 'billing',
  '/billing/invoices': 'billing',
  '/payment-gateways': 'payments',
  '/payment-dashboard': 'payments',
  '/payment-settings': 'payments',
  '/charges': 'payments',
  '/recurring': 'payments',
  '/users': 'users',
  '/customers': 'customers',
};

function loadGroupsState(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SIDEBAR_GROUPS_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // Coerce any non-boolean values to true to avoid silent bugs from
      // a hand-edited localStorage value.
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        out[k] = Boolean(v);
      }
      return out;
    }
  } catch { /* malformed JSON — fall through to empty */ }
  return {};
}

function saveGroupsState(state: Record<string, boolean>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_GROUPS_LS_KEY, JSON.stringify(state));
  } catch { /* quota / private-mode — non-fatal, lose persistence only */ }
}

export function PortalShell() {
  const user = useAuth((s) => s.user);
  const customerContext = useCustomerContext((s) => s.current);
  const resellerContext = useResellerContext((s) => s.current);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Per-group open/closed state. Missing keys → fall back to "auto-open
  // iff the group contains the active route" (computed inline below).
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadGroupsState);
  const toggleGroup = useCallback((title: string) => {
    setOpenGroups((prev) => {
      // Resolve the current effective open-ness so the toggle actually
      // flips what the user sees, not what we last persisted.
      const wasOpen = title in prev ? prev[title]! : false;
      const next = { ...prev, [title]: !wasOpen };
      saveGroupsState(next);
      return next;
    });
  }, []);
  const brand = useBranding();
  useLanguage();
  const { isEnabled, previewing } = useFeatureFlag();
  // Per-user module permissions (agents / limited users): hides nav items
  // whose scope.read was explicitly revoked. No-op for every other role.
  const { canReadScope } = useMyModulePermissions();
  // Mounted once here (not per-page) so the sidebar badge stays current and
  // the "New SMS message" toast fires anywhere in the portal. The hook
  // self-gates on role + the 'sms' feature flag.
  const { count: smsUnreadCount } = useSmsUnread();
  if (!user) { return <Navigate to="/login" replace />; }

  async function doLogout() {
    try { await logout(); } catch { /* server unreachable — clear locally anyway */ }
    useAuth.getState().clear();
    // Clear the entered-reseller + customer scopes too — the next user on
    // this machine shouldn't inherit whose scope the previous user was in.
    useCustomerContext.getState().exit();
    useResellerContext.getState().exit();
    navigate('/login');
  }

  // Super_admins only see customer-scoped nav items once they've drilled in
  // to both a reseller AND a customer. Reseller_owner only needs a customer
  // context (their reseller is implicit).
  function navItemGated(item: NavItem): boolean {
    // Reseller-level settings (branding, reseller email, etc.) disappear
    // once the user has drilled into a customer — they belong to the
    // reseller scope, not the customer being managed.
    if (item.hideInCustomerContext && customerContext) return false;

    if (!item.customerScoped) return true;
    if (user!.role === 'super_admin') {
      return Boolean(resellerContext && customerContext);
    }
    if (user!.role === 'reseller_owner') {
      return Boolean(customerContext);
    }
    // office_manager / standard_user / limited_user / agent have an
    // implicit single-customer scope from the auth store.
    return true;
  }

  // When super_admin or reseller_owner has "entered" a customer, the sidebar
  // collapses to show ONLY that customer's scoped items. Cross-customer items
  // (Resellers, DIDs, Carriers, Plans, global Settings, etc.) are hidden until
  // the user explicitly exits the customer context.
  //
  // Dashboard (/dashboard) is not marked customerScoped but is always shown —
  // it is a universal landing page regardless of context.
  const isInCustomerContext =
    Boolean(customerContext) &&
    (user!.role === 'super_admin' || user!.role === 'reseller_owner');

  // When super_admin has "entered" a reseller (but not a customer), the sidebar
  // hides platform-owner-only items so the focus stays on managing THAT
  // reseller's world (their customers, DIDs, branding, email, etc.). Once they
  // also enter a customer, the customer-context filter above takes over.
  // Reseller_owner doesn't need this: they have implicit reseller scope from
  // their JWT, and platformOnly items already reject their role via show().
  const isSuperAdminInResellerContext =
    user!.role === 'super_admin'
    && Boolean(resellerContext)
    && !customerContext;

  function shouldShowInCustomerContext(item: NavItem): boolean {
    if (!isInCustomerContext) return true; // no filtering for other roles or no context
    // Items pinned to "always show" — Dashboard, Account, Documentation —
    // aren't tied to any tenant scope and should appear in every sidebar
    // mode. Without this exemption Account would silently vanish the
    // moment an admin / reseller drilled into a customer (Sam called this
    // out 2026-05-15).
    if (item.alwaysVisible) return true;
    // Hide any item that is NOT customer-scoped
    return Boolean(item.customerScoped);
  }

  function shouldShowInResellerContext(item: NavItem): boolean {
    if (!isSuperAdminInResellerContext) return true; // not the SA-in-reseller case
    if (item.alwaysVisible) return true;
    // Hide platform-owner-only items inside the reseller bubble
    return !item.platformOnly;
  }

  const sidebar = (
    <>
      <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
        {brand.logo_url && (
          <img
            src={brand.logo_url}
            alt=""
            className="h-7 w-7 rounded object-contain"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <span className="font-semibold tracking-tight truncate">{brand.brand_name}</span>
      </div>
      <nav aria-label="Primary" className="flex-1 py-4 px-2 space-y-4 overflow-y-auto">
        {/* When drilled into a customer, show a sticky label above the nav items. */}
        {isInCustomerContext && customerContext && (
          <div className="mx-1 mb-1 rounded-md bg-primary/10 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/70 leading-none mb-0.5">
              Customer
            </p>
            <p className="text-sm font-medium text-primary truncate">
              {customerContext.name}
            </p>
          </div>
        )}
        {NAV_GROUPS.map((group, gi) => {
          const visibleItems = group.items.filter((n) => {
            if (!n.show(user.role, user.is_super_admin)) return false;
            // Customer-scoped items hide until the drill-down is complete
            // (applies to super_admin + reseller_owner only — see navItemGated).
            if (!navItemGated(n)) return false;
            // When inside a customer context, hide cross-customer items.
            if (!shouldShowInCustomerContext(n)) return false;
            // When super_admin has entered a reseller (no customer yet), hide
            // platform-owner-only items so the sidebar focuses on managing
            // that reseller.
            if (!shouldShowInResellerContext(n)) return false;
            // Super admin always sees everything else, regardless of feature
            // Per-user permission matrix (agents / limited users only): hide
            // items whose module scope has read explicitly revoked.
            const permScope = NAV_PERMISSION_SCOPES[n.to];
            if (permScope && !canReadScope(permScope)) return false;
            // flags — EXCEPT while previewing an entered customer, where the
            // nav reflects that customer's real flags.
            if (user.is_super_admin && !previewing) return true;
            if (n.feature && !isEnabled(n.feature)) return false;
            return true;
          });
          if (visibleItems.length === 0) return null;

          // Resolve the href the same way the NavLink does so we can detect
          // the active group BEFORE rendering (groups containing the current
          // route auto-expand). Mirrors the scopedHref call in the item
          // loop below — keep them in sync.
          const itemHrefs = visibleItems.map((n) => {
            // Customer-tier DTMF profiles (SA / reseller_owner inside a
            // customer context): the per-customer editor lives at
            // /customers/:id/settings/dtmf-profiles (uuid param — not a
            // scoped-URL child), so build the href from the entered
            // customer's id instead of the scoped-prefix helper.
            if (n.to === '/settings/dtmf-profiles' && n.customerScoped && customerContext) {
              return `/customers/${customerContext.id}/settings/dtmf-profiles`;
            }
            // Feature flags inside a customer context → that customer's
            // Manage page, where the per-account features card lives.
            if (n.to === '/settings/features' && n.customerScoped && customerContext) {
              return `/customers/${customerContext.id}`;
            }
            return scopedHref(n.to, {
              isSuperAdmin: Boolean(user.is_super_admin) || user.role === 'super_admin',
              customerNumber: customerContext?.customerNumber ?? null,
              resellerNumber: resellerContext?.resellerNumber ?? null,
            });
          });
          const groupHasActive = itemHrefs.some((href) =>
            location.pathname === href || location.pathname.startsWith(`${href}/`),
          );

          // Resolution rule for "is this group open":
          //   - User has explicitly toggled → honor that boolean.
          //   - Otherwise → auto-open iff the active route lives in this group.
          // Untitled groups (the first "Dashboard" cluster) skip the collapse
          // wrapper entirely — there's nothing to label, so nothing to hide
          // behind.
          const userToggled = group.title !== null && group.title in openGroups;
          const isOpen = group.title === null
            ? true
            : userToggled ? openGroups[group.title!]! : groupHasActive;

          const itemsList = (
            <div className="space-y-0.5">
              {visibleItems.map((n, i) => {
                const href = itemHrefs[i]!;
                return (
                  <NavLink
                    key={n.to}
                    to={href}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => cn(
                      // Indent slightly when inside a titled group so the
                      // sub-button relationship reads visually.
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                      group.title !== null && 'pl-7',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
                    )}
                  >
                    <n.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{n.label}</span>
                    {/* Unread-SMS count. Same pill shape as the group count
                        badge above, but destructive-red so it reads as "needs
                        attention". ms-auto (not ml-) keeps it RTL-safe. */}
                    {n.to === '/sms' && smsUnreadCount > 0 && (
                      <span
                        aria-label={`${smsUnreadCount} unread`}
                        className="ms-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold tabular-nums"
                      >
                        {smsUnreadCount > 99 ? '99+' : smsUnreadCount}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );

          if (group.title === null) {
            // Untitled cluster: render flat, no collapse affordance.
            return <div key={gi}>{itemsList}</div>;
          }

          // Titled groups: render the title as a button that toggles
          // expand/collapse, with a chevron that rotates to indicate state.
          const groupId = `sidebar-group-${gi}`;
          return (
            <div key={gi}>
              <button
                type="button"
                onClick={() => toggleGroup(group.title!)}
                aria-expanded={isOpen}
                aria-controls={groupId}
                className={cn(
                  'group/header w-full flex items-center gap-2 px-3 py-2 rounded-md',
                  // Larger + bolder than the previous 10px tracking-widest.
                  // The old label felt anemic; this one reads like an
                  // actual section heading you'd click.
                  'text-[13px] font-semibold tracking-tight',
                  // Closed groups: medium-weight foreground.
                  // Open groups: full-strength foreground with a subtle
                  // tinted background so the active section stands out
                  // from collapsed neighbours.
                  isOpen
                    ? 'text-foreground bg-secondary/40'
                    : 'text-foreground/70 hover:text-foreground hover:bg-secondary/30',
                  'transition-colors',
                )}
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0 text-muted-foreground/70 transition-transform duration-150',
                    isOpen && 'rotate-90 text-foreground/80',
                  )}
                />
                <span className="truncate">{group.title}</span>
                {/* Count badge — small pill on the right showing how
                    many items live in this group. Always visible (not
                    just when collapsed) so the user has a sense of
                    section weight without having to expand each one. */}
                <span className={cn(
                  'ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full',
                  'text-[10px] font-medium tabular-nums',
                  isOpen
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground/70',
                )}>
                  {visibleItems.length}
                </span>
              </button>
              {isOpen && (
                <div id={groupId} className="mt-1 mb-1">
                  {itemsList}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border text-xs">
        <div className="truncate font-medium text-foreground">{user.email}</div>
        <div className="text-muted-foreground flex items-center gap-1 mt-0.5">
          <span className="capitalize">{user.role.replace(/_/g, ' ')}</span>
          {user.mfa_enabled && <span className="text-primary">· MFA</span>}
        </div>
        <button
          onClick={doLogout}
          className="mt-3 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> Log out
        </button>
      </div>
    </>
  );

  return (
    <ForcePasswordChangeGuard>
      <div className="min-h-screen lg:grid lg:grid-cols-[16rem_1fr] bg-background">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 bg-background border border-border px-3 py-1.5 rounded-md text-sm"
        >
          Skip to content
        </a>

        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-border bg-background">
          <span className="font-semibold tracking-tight">Ivrello</span>
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            <button
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="absolute inset-0 bg-black/40"
            />
            <aside className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-background border-r border-border flex flex-col">
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-secondary"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
              {sidebar}
            </aside>
          </div>
        )}

        {/* Desktop sidebar */}
        <aside className="hidden lg:flex border-r border-border flex-col sticky top-0 h-screen">
          {sidebar}
        </aside>

        <main id="main-content" className="min-w-0">
          {/* Banner stack, top → bottom, by priority:
              0. Deploy failure (red) — system is broken, must fix first
              1. Masquerade (red) — most-urgent: you're driving someone else's session
              2. Reseller context (indigo) — super_admin drilled into a reseller
              3. Customer context (blue) — drilled into a customer */}
          <DeployStatusBanner />
          <MasqueradeBanner />
          <ResellerContextBanner />
          <CustomerContextBanner />
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </main>

        {/* Global toast viewport — required for useToast() calls anywhere in
            the portal (SMS notifications, conference actions, etc.) to
            actually render. Fixed-positioned, so its placement inside the
            grid doesn't affect layout. */}
        <Toaster />
      </div>
    </ForcePasswordChangeGuard>
  );
}
